import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import ImageUploader from "./ImageUploader";

const CATEGORIES = ["Bodas", "Cumpleaños", "Comuniones", "Sesiones exteriores", "Estudio"];
const SPANS = [
    { value: "normal", label: "Normal (1×1)" },
    { value: "tall", label: "Alta (1×2)" },
    { value: "wide", label: "Ancha (2×1)" },
];

const emptyForm = {
    id: null,
    category: "Bodas",
    image: "",
    span: "normal",
    order: 0,
};

export default function PortfolioManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("Todo");
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/portfolio");
            setItems(data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        if (filter === "Todo") return items;
        return items.filter((i) => i.category === filter);
    }, [items, filter]);

    const openAdd = () => {
        setForm({ ...emptyForm, category: filter === "Todo" ? "Bodas" : filter });
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            id: item.id,
            category: item.category,
            image: item.image,
            span: item.span,
            order: item.order || 0,
        });
        setModalOpen(true);
    };

    const onSave = async (e) => {
        e.preventDefault();
        if (!form.image) {
            toast.error("Añade una imagen.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                category: form.category,
                image: form.image,
                span: form.span,
                order: Number(form.order) || 0,
            };
            if (form.id) {
                await api.put(`/portfolio/${form.id}`, payload);
                toast.success("Foto actualizada");
            } else {
                await api.post("/portfolio", payload);
                toast.success("Foto añadida");
            }
            setModalOpen(false);
            setForm(emptyForm);
            await load();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (item) => {
        if (!window.confirm("¿Eliminar esta foto del portfolio?")) return;
        try {
            await api.delete(`/portfolio/${item.id}`);
            toast.success("Foto eliminada");
            setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    return (
        <section data-testid="portfolio-manager">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                <div>
                    <h2 className="font-display text-3xl text-[#1C1A18]">
                        Gestión del Portfolio
                    </h2>
                    <p className="text-sm text-[#6b665f] font-light mt-2">
                        Añade, modifica o elimina las fotos por categoría.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openAdd}
                    data-testid="portfolio-add-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 btn-gold text-[10px] uppercase self-start"
                >
                    <Plus size={14} /> Nueva foto
                </button>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-8" data-testid="portfolio-filters">
                {["Todo", ...CATEGORIES].map((c) => (
                    <button
                        key={c}
                        type="button"
                        onClick={() => setFilter(c)}
                        data-testid={`portfolio-filter-${c.toLowerCase()}`}
                        className={`chip ${filter === c ? "active" : ""}`}
                    >
                        {c}{" "}
                        <span className="ml-1 text-[#C4A882]">
                            (
                            {c === "Todo"
                                ? items.length
                                : items.filter((i) => i.category === c).length}
                            )
                        </span>
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="animate-spin text-[#C4A882]" size={32} />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-[#6b665f] font-light text-sm">
                    No hay fotos en esta categoría todavía.
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filtered.map((item) => (
                        <article
                            key={item.id}
                            data-testid={`portfolio-item-${item.id}`}
                            className="group relative bg-white border border-[#f0ece4] overflow-hidden"
                        >
                            <div className="aspect-[4/5] bg-[#f0ece4]">
                                <img
                                    src={item.image}
                                    alt={item.category}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-3 flex items-center justify-between">
                                <div className="min-w-0">
                                    <p className="text-[10px] tracking-[0.32em] uppercase text-[#C4A882]">
                                        {item.category}
                                    </p>
                                    <p className="text-[10px] text-[#6b665f] mt-0.5 uppercase tracking-widest">
                                        {item.span} · #{item.order}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(item)}
                                        className="p-2 text-[#6b665f] hover:text-[#1C1A18]"
                                        aria-label="Editar"
                                        data-testid={`portfolio-edit-${item.id}`}
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onDelete(item)}
                                        className="p-2 text-[#6b665f] hover:text-red-600"
                                        aria-label="Eliminar"
                                        data-testid={`portfolio-delete-${item.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {modalOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="portfolio-modal"
                    className="fixed inset-0 z-[100] bg-[#1C1A18]/80 flex items-center justify-center p-4 overflow-y-auto"
                    onClick={() => !saving && setModalOpen(false)}
                >
                    <form
                        onSubmit={onSave}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#FAFAF8] w-full max-w-2xl my-12 p-8 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-8"
                    >
                        <div>
                            <h3 className="font-display text-2xl text-[#1C1A18] mb-4">
                                {form.id ? "Editar foto" : "Nueva foto"}
                            </h3>
                            <ImageUploader
                                value={form.image}
                                onChange={(v) => setForm((f) => ({ ...f, image: v }))}
                                testIdPrefix="portfolio-image"
                            />
                        </div>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Categoría
                                </label>
                                <select
                                    value={form.category}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, category: e.target.value }))
                                    }
                                    className="field"
                                    data-testid="portfolio-category-select"
                                >
                                    {CATEGORIES.map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Tamaño en la cuadrícula
                                </label>
                                <select
                                    value={form.span}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, span: e.target.value }))
                                    }
                                    className="field"
                                    data-testid="portfolio-span-select"
                                >
                                    {SPANS.map((s) => (
                                        <option key={s.value} value={s.value}>
                                            {s.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Orden (menor = primero)
                                </label>
                                <input
                                    type="number"
                                    value={form.order}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, order: e.target.value }))
                                    }
                                    className="field"
                                    data-testid="portfolio-order-input"
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 pt-4">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="portfolio-save-btn"
                                    className="flex-1 px-6 py-3 btn-gold text-[10px] uppercase disabled:opacity-60"
                                >
                                    {saving ? "Guardando…" : form.id ? "Guardar cambios" : "Añadir foto"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    disabled={saving}
                                    data-testid="portfolio-cancel-btn"
                                    className="px-6 py-3 btn-outline-ink text-[10px] uppercase"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
}
